import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  cloneElement,
  isValidElement,
  useRef,
  type CSSProperties,
  type HTMLAttributes,
  type MouseEvent as ReactMouseEvent,
  type ReactElement,
  type ReactNode,
} from 'react';

/**
 * Wraps a DataTable `<tr>` with dnd-kit sortable behavior (vertical reorder).
 * Pass the `<tr>` from `rowTrWrapper` as `children`.
 */
export function DataTableSortableTr({
  id,
  children,
  disabled,
  onRowClickGuard,
}: {
  id: string;
  children: ReactElement<HTMLAttributes<HTMLTableRowElement>>;
  disabled?: boolean;
  /** When true, suppresses click after drag (taxonomy structure pattern). */
  onRowClickGuard?: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled,
  });
  const dragMovedRef = useRef(false);

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.35 : undefined,
    zIndex: isDragging ? 2 : undefined,
    cursor: disabled ? 'progress' : isDragging ? 'grabbing' : 'grab',
    position: 'relative',
    userSelect: 'none',
    touchAction: 'none',
    boxShadow: isDragging
      ? '0 8px 28px color-mix(in srgb, var(--wp-brunswick-900) 14%, transparent)'
      : undefined,
  };

  if (isDragging && onRowClickGuard && !dragMovedRef.current) {
    dragMovedRef.current = true;
  }

  if (!isValidElement(children)) return children as ReactNode;

  const childOnClick = children.props.onClick;
  const mergedOnClick = childOnClick
    ? (e: ReactMouseEvent<HTMLTableRowElement>) => {
        if (onRowClickGuard && dragMovedRef.current) {
          dragMovedRef.current = false;
          return;
        }
        if (isDragging) return;
        childOnClick(e);
      }
    : childOnClick;

  const sortableRowProps = {
    ref: setNodeRef,
    style: { ...(children.props.style ?? {}), ...style },
    ...attributes,
    ...listeners,
    'data-sortable-id': id,
    'data-dragging': isDragging ? 'true' : undefined,
    onPointerDownCapture: onRowClickGuard
      ? () => {
          dragMovedRef.current = false;
        }
      : children.props.onPointerDownCapture,
    onClick: mergedOnClick,
  } as HTMLAttributes<HTMLTableRowElement>;

  return cloneElement(children, sortableRowProps);
}
